import React from 'react'
import { useAppSelector } from '../redux'
import { v4 as uuidv4 } from 'uuid'

import {
  getUserNotes,
  deleteUserNote,
  postUserNote,
  putUserNote
} from '../../lib/api'
import { selectAccess } from '../../slices/accessSlice'
import useUserPermissions from '../permissions/useUserPermissions'

const MAX_LENGTH = 2048 // API enforced max note length

const newEntry = () => ({ [uuidv4()]: { content: '', isOld: false } })

const formatAPINote = note => ({ ...note, content: note.content, isOld: true })

const formatNoteForAPI = (note, id) => {
  const { content, isOld } = note
  if ((content ?? '').trim().length > 0) {
    const newNote = { content }
    if (isOld) {
      newNote.id = id
    }
    return newNote
  }
  return null
}

// Giving a userID will cause notes to be loaded from that id
const useNotes = userID => {
  const { loaded: accessLoaded } = useAppSelector(selectAccess)

  const { hasBasic } = useUserPermissions()

  const [notes, setNotes] = React.useState({ ...newEntry() })
  const [toSave, setToSave] = React.useState([])
  const [updateIDs, setUpdateIDs] = React.useState([])
  const notesCache = React.useRef({})

  // Formats notes for api
  const apiNotes = React.useMemo(() => {
    const mapped = []
    Object.entries(notes).forEach(([id, note]) => {
      const formatted = formatNoteForAPI(note, id)
      if (formatted) {
        mapped.push(formatted)
      }
    })
    return mapped
  }, [notes])

  // Load notes from api
  React.useEffect(() => {
    if (!userID || !accessLoaded || !hasBasic) {
      return
    }

    const handleGetNotes = async () => {
      // Get cached version to save network calls
      if (userID in notesCache.current) {
        return notesCache.current[userID]
      }

      const notes = await getUserNotes(userID)
      const mappedNotes = notes.map(note => ({
        id: note.id,
        ...formatAPINote(note)
      }))
      // Convert array of notes into id => note map
      const formattedNotes = mappedNotes.reduce(
        (acc, { id, ...rest }) => ({ ...acc, [id]: rest }),
        {}
      )

      // Store in cache
      notesCache.current[userID] = formattedNotes

      return formattedNotes
    }

    const timer = setTimeout(() => {
      handleGetNotes()
        .then(setNotes)
        .catch(err => console.error('Error in note hook fetch', err))
    }, 500)
    return () => {
      clearTimeout(timer)
    }
  }, [userID, accessLoaded, hasBasic])

  // Saves notes in API
  React.useEffect(() => {
    if (!toSave.length) {
      return
    }

    let mounted = true

    // POST/PUT all notes
    Promise.all(
      toSave.map(async data => {
        const { id, formatted, isOld } = data
        if (!formatted) {
          return
        }

        try {
          if (isOld) {
            await putUserNote(userID, formatted)
            return
          }

          const res = await postUserNote(userID, formatted)
          mounted && setUpdateIDs(old => [...old, { oldID: id, newID: res.id }])
        } catch (err) {
          console.error('Error posting note', err)
        }
      })
    ).then(() => mounted && setToSave([]))

    return () => {
      mounted = false
    }
  }, [toSave, userID])

  // Fixes IDs on notes
  // handles the case of a note being POSTed and needing to use the id returned from the api
  React.useEffect(() => {
    if (!updateIDs.length) {
      return
    }
    const toSwap = [...updateIDs]
    setUpdateIDs([])

    setNotes(old => {
      const newNotes = {}

      const toExclude = toSwap.map(({ oldID }) => oldID)

      // Removed old ids
      Object.entries(old).forEach(([key, val]) => {
        if (!toExclude.includes(key)) {
          newNotes[key] = { ...val }
        }
      })

      // Update new ids to have old content
      toSwap.forEach(({ oldID, newID }) => {
        newNotes[newID] = {
          ...old[oldID],
          id: newID,
          isOld: true,
          //once we get the correct id, we set a more accurate stamp
          stamp: new Date().toISOString()
        }
      })

      return newNotes
    })
  }, [updateIDs])

  // Resets notes to starting state
  const clearNotes = React.useCallback(() => {
    setNotes({ ...newEntry() })
  }, [])

  // Updates a note by id
  const handleChangeNote = React.useCallback(
    id =>
      (val, save = false) => {
        const content = val.slice(0, MAX_LENGTH)
        //add 10 seconds between new notes to sort them
        const t = new Date()
        t.setSeconds(t.getSeconds() + 10)
        setNotes(old => {
          const newNotes = {
            ...old,
            [id]: {
              ...old[id],
              content,
              stamp: old[id]?.stamp || t.toISOString()
            }
          }

          // Queues a note to be save if it is not already in the queue
          if (save) {
            const current = { ...newNotes[id] }
            const formatted = formatNoteForAPI(current, id)
            setToSave(old => {
              const newObj = { id, formatted, isOld: current.isOld }
              if (old.some(obj => obj.id === newObj.id)) {
                return old
              }

              return [...old, newObj]
            })
          }

          // Update cache
          notesCache.current[userID] = newNotes

          return newNotes
        })
      },
    [userID]
  )

  // Creates a new blank note
  const addNote = React.useCallback(() => {
    setNotes(old => ({ ...old, ...newEntry() }))
  }, [])

  // Deletes a note locally and in api if needed
  const deleteNote = React.useCallback(
    id => () => {
      setNotes(old => {
        const note = old[id]
        if (note.isOld) {
          deleteUserNote(userID, id)
        }

        const newNotes = {}
        Object.entries(old).forEach(([noteID, val]) => {
          if (noteID !== id) {
            newNotes[noteID] = { ...val }
          }
        })

        // Update cache
        notesCache.current[userID] = newNotes

        return newNotes
      })
    },
    [userID]
  )

  return { notes, apiNotes, clearNotes, handleChangeNote, addNote, deleteNote }
}

export default useNotes
